/**
 * Version Check Utility
 * Checks if a new version of the application is available
 *
 * Single source of truth: /version.json on the server
 */

import { BUILD_VERSION } from '../constants/version';
import { logger } from './logger';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_FILE = '/version.json';

export interface VersionInfo {
  version: string;
  timestamp: number;
}

/**
 * Get the current version from the build
 */
export function getCurrentVersion(): string {
  return BUILD_VERSION;
}

/**
 * Fetch the latest version from the server
 */
async function fetchLatestVersion(): Promise<string | null> {
  try {
    const response = await fetch(VERSION_FILE, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    if (!response.ok) {
      logger.warn('Failed to fetch version info', { statusText: response.statusText });
      return null;
    }

    const data: VersionInfo = await response.json();
    return data.version;
  } catch (error) {
    logger.warn('Error fetching version info', { error });
    return null;
  }
}

/**
 * Compare two version strings (format: YYYYMMDD.buildnumber)
 * Returns true if latestVersion is newer than currentVersion
 */
function isNewerVersion(currentVersion: string, latestVersion: string): boolean {
  // Skip version check in development (when version is placeholder)
  if (currentVersion === '__VERSION__' || latestVersion === '__VERSION__') {
    return false;
  }

  // Parse version format: YYYYMMDD.buildnumber
  const parseVersion = (v: string) => {
    const [datePart, buildPart] = v.split('.');
    return {
      date: parseInt(datePart, 10),
      build: parseInt(buildPart || '0', 10),
    };
  };

  try {
    const current = parseVersion(currentVersion);
    const latest = parseVersion(latestVersion);

    // Compare date first
    if (latest.date > current.date) {
      return true;
    }

    // If same date, compare build number
    if (latest.date === current.date && latest.build > current.build) {
      return true;
    }

    return false;
  } catch (error) {
    logger.error('Error comparing versions', { error });
    return false;
  }
}

/**
 * Check if a new version is available
 */
export async function checkForNewVersion(): Promise<{
  hasNewVersion: boolean;
  currentVersion: string;
  latestVersion: string | null;
}> {
  const currentVersion = getCurrentVersion();
  const latestVersion = await fetchLatestVersion();

  const hasNewVersion = latestVersion !== null && isNewerVersion(currentVersion, latestVersion);

  return {
    hasNewVersion,
    currentVersion,
    latestVersion,
  };
}

/**
 * Start periodic version checking
 * @param onNewVersion Callback when a new version is detected
 * @returns Cleanup function to stop checking
 */
export function startVersionCheck(onNewVersion: (latestVersion: string) => void): () => void {
  let intervalId: number | null = null;
  let isChecking = false;

  const checkVersion = async () => {
    // Prevent concurrent checks
    if (isChecking) {
      return;
    }

    isChecking = true;
    try {
      const result = await checkForNewVersion();
      if (result.hasNewVersion && result.latestVersion) {
        logger.info(
          `New version available: ${result.latestVersion} (current: ${result.currentVersion})`
        );
        onNewVersion(result.latestVersion);
      }
    } catch (error) {
      logger.error('Error during version check', { error });
    } finally {
      isChecking = false;
    }
  };

  // Check immediately on start
  checkVersion();

  // Then check periodically
  intervalId = window.setInterval(checkVersion, VERSION_CHECK_INTERVAL);

  // Return cleanup function
  return () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };
}

/**
 * Reload the page to load the new version
 */
export function reloadForNewVersion(): void {
  window.location.reload();
}
